import { useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import {
  aggregateDepartmentMonth,
  aggregateAgencyMonth,
} from "../../lib/calculations";
import { formatUSD } from "../../lib/formatters";
import { today } from "../../lib/dateUtils";
import DepartmentProfile from "./DepartmentProfile";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Department dashboard.
//
// Outside view: 12-month matrix where each row is a department and each cell
// shows that department's NET profit for the month. Bottom row = agency total.
// Click any row to drill into the department profile (offers, costs, partners).
//
// "Unassigned" is a synthetic department for offers without a department tag —
// shown only when it has any activity, to nudge the user to fix the tagging.
export default function Departments({ state, dateFilter }) {
  const [selectedDept, setSelectedDept] = useState(null);
  const departments = state.settings?.departments || [];

  // If the Dashboard handed us a pre-selected department via localStorage,
  // pick it up on mount and clear the handoff.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const pending = window.localStorage.getItem("uc.pendingDept");
    if (pending && departments.includes(pending)) {
      setSelectedDept(pending);
      window.localStorage.removeItem("uc.pendingDept");
    }
  }, [departments]);

  const currentYear = today().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonth = dateFilter?.toMonth || 12;
  const currentMonthIdx = today().getMonth();

  // Per-department monthly P&L for the selected year (12 months × N depts).
  const rows = useMemo(() => {
    const allDepts = [...departments];
    const out = [];
    for (const dept of allDepts) {
      const months = new Array(12).fill(null);
      for (let i = 0; i < 12; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        months[i] = aggregateDepartmentMonth(dept, state, mKey);
      }
      out.push({ dept, months });
    }
    // Synthetic "Unassigned" row — only if it has activity.
    const unassigned = new Array(12).fill(null);
    let unassignedActivity = 0;
    for (let i = 0; i < 12; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      const agg = aggregateDepartmentMonth(null, state, mKey);
      unassigned[i] = agg;
      unassignedActivity += Math.abs(agg.revenue) + Math.abs(agg.cogs.total) + Math.abs(agg.opex.total);
    }
    if (unassignedActivity > 0.5) {
      out.push({ dept: "__unassigned__", label: "Unassigned (no department tag)", months: unassigned });
    }
    return out;
  }, [departments, state, year]);

  const agencyMonths = useMemo(() => {
    const out = new Array(12).fill(null);
    for (let i = 0; i < 12; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      out[i] = aggregateAgencyMonth(state, mKey);
    }
    return out;
  }, [state, year]);

  // Totals per dept across the selected month range. Capped at the current
  // month for the active year so we never sum empty future months.
  const cutoffMonthIdx = year === currentYear ? currentMonthIdx : 11;
  const startIdx = Math.max(0, fromMonth - 1);
  const endIdx = Math.min(toMonth - 1, cutoffMonthIdx);
  const isYtdDefault = year === currentYear && fromMonth === 1 && toMonth - 1 >= currentMonthIdx;
  const totalLabel = isYtdDefault
    ? "Net YTD"
    : fromMonth === toMonth
    ? `Net ${MONTH_LABELS[fromMonth - 1]}`
    : `Net ${MONTH_LABELS[fromMonth - 1]}–${MONTH_LABELS[toMonth - 1]}`;
  const breakdownLabel = isYtdDefault
    ? `YTD breakdown · ${year}`
    : fromMonth === toMonth
    ? `${MONTH_LABELS[fromMonth - 1]} breakdown · ${year}`
    : `${MONTH_LABELS[fromMonth - 1]}–${MONTH_LABELS[toMonth - 1]} breakdown · ${year}`;

  const rowTotal = (months) => {
    let revenue = 0, cogsTotal = 0, opexTotal = 0, partner = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      revenue += months[i]?.revenue || 0;
      cogsTotal += months[i]?.cogs.total || 0;
      opexTotal += months[i]?.opex.total || 0;
      partner += months[i]?.partnerCutsOwed || 0;
    }
    return {
      revenue,
      cogsTotal,
      opexTotal,
      partner,
      gross: revenue - cogsTotal,
      net: revenue - cogsTotal - opexTotal,
      netAfterPartner: revenue - cogsTotal - opexTotal - partner,
    };
  };

  if (selectedDept) {
    return (
      <DepartmentProfile
        department={selectedDept === "__unassigned__" ? null : selectedDept}
        displayName={selectedDept === "__unassigned__" ? "Unassigned" : selectedDept}
        state={state}
        dateFilter={dateFilter}
        onBack={() => setSelectedDept(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-brand-blue" />
        <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">Departments · {year}</h2>
        <span className="text-xs text-brand-slate">Net profit per department, per month. Click a row to drill in.</span>
      </div>

      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col style={{ width: "20%" }} />
              {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${72 / 12}%` }} />)}
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Department</th>
                {MONTH_LABELS.map((m, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <th key={m} className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`}>{m}</th>
                  );
                })}
                <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">{totalLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ dept, label, months }) => {
                const totals = rowTotal(months);
                const displayName = label || dept;
                return (
                  <tr key={dept} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
                    <td
                      className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue"
                      onClick={() => setSelectedDept(dept)}
                      title="Open department profile"
                    >
                      {displayName}
                    </td>
                    {months.map((mAgg, i) => {
                      const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                      const isCurrent = i === currentMonthIdx && year === currentYear;
                      const isFuture = i > cutoffMonthIdx;
                      const net = mAgg ? (mAgg.revenue - mAgg.cogs.total - mAgg.opex.total) : 0;
                      const color = !inRange || isFuture
                        ? "text-gray-300"
                        : net > 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : net < 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-300";
                      return (
                        <td key={i} className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${color}`}>
                          {isFuture || Math.abs(net) < 0.5 ? "—" : formatUSD(net)}
                        </td>
                      );
                    })}
                    <td className={`px-2 py-2 text-right font-semibold border-l border-gray-100 dark:border-[var(--border-soft)] ${
                      totals.net > 0 ? "text-emerald-700 dark:text-emerald-300" :
                      totals.net < 0 ? "text-red-600 dark:text-red-400" :
                      "text-brand-slate"
                    }`}>
                      {Math.abs(totals.net) < 0.5 ? "—" : formatUSD(totals.net)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 dark:border-[var(--border-soft)] bg-brand-ink text-white">
                <td className="px-3 py-2 font-bold border-r border-white/10">Agency total</td>
                {agencyMonths.map((mAgg, i) => {
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  const isFuture = i > cutoffMonthIdx;
                  const net = mAgg ? (mAgg.revenue - mAgg.cogs.total - mAgg.opex.total) : 0;
                  return (
                    <td key={i} className={`px-1 py-2 text-center font-bold ${isCurrent ? "text-brand-mint" : isFuture ? "text-white/30" : "text-white"}`}>
                      {isFuture || Math.abs(net) < 0.5 ? "—" : formatUSD(net)}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-bold border-l border-white/10">
                  {(() => {
                    let total = 0;
                    for (let i = 0; i <= cutoffMonthIdx; i++) {
                      total += (agencyMonths[i]?.revenue || 0) - (agencyMonths[i]?.cogs.total || 0) - (agencyMonths[i]?.opex.total || 0);
                    }
                    return Math.abs(total) < 0.5 ? "—" : formatUSD(total);
                  })()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* YTD breakdown table */}
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)]">
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
            {breakdownLabel}
          </div>
          <div className="text-xs text-brand-slate mt-0.5">
            Revenue → minus COGS → minus OPEX → minus partner cuts owed = remaining for owner.
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Department</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Revenue</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">COGS</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Gross</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">OPEX</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Net</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">Partner cuts</th>
              <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-brand-slate">After partner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ dept, label, months }) => {
              const totals = rowTotal(months);
              const displayName = label || dept;
              return (
                <tr key={dept} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)] cursor-pointer" onClick={() => setSelectedDept(dept)}>
                  <td className="px-4 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)]">{displayName}</td>
                  <td className="px-4 py-2 text-right">{formatUSD(totals.revenue)}</td>
                  <td className="px-4 py-2 text-right text-brand-slate">{totals.cogsTotal > 0 ? `−${formatUSD(totals.cogsTotal)}` : "—"}</td>
                  <td className={`px-4 py-2 text-right font-medium ${totals.gross >= 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-red-600"}`}>{formatUSD(totals.gross)}</td>
                  <td className="px-4 py-2 text-right text-brand-slate">{totals.opexTotal > 0 ? `−${formatUSD(totals.opexTotal)}` : "—"}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${
                    totals.net > 0 ? "text-emerald-700 dark:text-emerald-300" :
                    totals.net < 0 ? "text-red-600 dark:text-red-400" : ""
                  }`}>{formatUSD(totals.net)}</td>
                  <td className="px-4 py-2 text-right text-purple-700 dark:text-purple-300">{totals.partner > 0 ? `−${formatUSD(totals.partner)}` : "—"}</td>
                  <td className={`px-4 py-2 text-right font-bold ${
                    totals.netAfterPartner > 0 ? "text-emerald-700 dark:text-emerald-300" :
                    totals.netAfterPartner < 0 ? "text-red-600 dark:text-red-400" : ""
                  }`}>{formatUSD(totals.netAfterPartner)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
